import React from 'react';
import { Button, Header3Text, PageLoading } from 'ui-toolkit';
import { FieldArray, Form, Formik, FormikHelpers } from 'formik';
import { assertDefined } from 'utilities/Assertions';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus } from '@fortawesome/pro-solid-svg-icons';
import { StyledFeeSection, StyledFeeSectionHeader } from '../FeeSections.Styled';
import { IncomingWireTable } from './IncomingWireTable';
import { IWireDepositData } from 'types/order/escrow/IWireDepositData';
import { IncomingWireDepositValidationSchema } from './IncomingWireDepositValidationSchema';
import {
  useCreateWireDepositMutation,
  useDeleteWireDepositMutation,
  useUpdateWireDepositMutation,
} from 'API/order/escrow/EscrowBaseAPI';
import { checkForMutationSuccess } from 'API/checkForMutationSuccess';
import { StyledIncomingFlexRow } from './StyledIncomingFlexRow';

interface IIncomingWireSection {
  wireDeposits?: IWireDepositData[];
  orderId: string;
  escrowFileId?: string;
}

export const IncomingWireSection: React.FC<IIncomingWireSection> = ({ wireDeposits, orderId, escrowFileId }) => {
  assertDefined(wireDeposits);
  assertDefined(escrowFileId);

  const [onCreate, { isLoading: isCreating }] = useCreateWireDepositMutation();
  const [onUpdate, { isLoading: isUpdating }] = useUpdateWireDepositMutation();
  const [onDelete, { isLoading: isDeleting }] = useDeleteWireDepositMutation();

  const onSave = async (values: { wireDeposits: IWireDepositData[] }, { setFieldValue }: FormikHelpers<any>) => {
    values.wireDeposits.forEach(async (deposit, index) => {
      if (!deposit.isEditing) {
        return;
      }
      if (deposit.createdAt) {
        const updateResult = await onUpdate({ ...deposit, orderId, escrowFileId });
        if (checkForMutationSuccess(updateResult, 'updating wire')) {
          setFieldValue(`wireDeposits.${index}.isEditing`, false);
        }
      } else {
        const result = await onCreate({ ...deposit, orderId, escrowFileId });
        if (checkForMutationSuccess(result, 'creating wire')) {
          setFieldValue(`wireDeposits.${index}.isEditing`, false);
        }
      }
    });
  };

  return (
    <StyledFeeSection>
      <PageLoading isLoading={isCreating || isUpdating || isDeleting} />
      <StyledFeeSectionHeader>
        <Header3Text>Incoming Wire</Header3Text>
      </StyledFeeSectionHeader>
      <Formik
        initialValues={{ wireDeposits }}
        validationSchema={IncomingWireDepositValidationSchema}
        onSubmit={onSave}
        enableReinitialize
      >
        {({ values, handleSubmit }) => (
          <Form onSubmit={handleSubmit}>
            <FieldArray name={'wireDeposits'}>
              {({ push, remove }) => (
                <>
                  <IncomingWireTable onDelete={(id) => onDelete({ id, orderId })} remove={remove} />
                  <StyledIncomingFlexRow>
                    <Button
                      intent={'primary'}
                      onClick={() => push({ version: 'v2-fees', isEditing: true })}
                      data-test-id={'add-wire-button'}
                    >
                      <FontAwesomeIcon icon={faPlus} /> Add Wire
                    </Button>
                    {values.wireDeposits.length > 0 && (
                      <Button intent={'secondary'} type={'submit'} data-test-id={'update-wire-button'}>
                        Update Escrow File
                      </Button>
                    )}
                  </StyledIncomingFlexRow>
                </>
              )}
            </FieldArray>
          </Form>
        )}
      </Formik>
    </StyledFeeSection>
  );
};
